// custom imports
import { UserState } from ".";
import {
  abortAllRequests,
  assertBillingWriteAllowed,
  getCookie,
  getCsrfToken,
  normalizeIdentityEmail,
  normalizePhone,
  suppressUnauthorizedFor,
} from "../../utils";
import { isDemoActive } from "../../vend/state/demoFlag";
import {
  getUser,
  fetchOrganization,
  requestSecurityChallenge,
  confirmSecurityChallenge,
  setCsrfToken,
  uploadOrganizationLogo,
  removeOrganizationLogo,
  setDefaultOrganizationLogo,
  updateOrganization,
  updateUserProfile,
  url,
} from "../api";
import {
  attachBankAccount,
  attachPaymentMethod,
  getBankAccountClientSecret,
  getPaymentMethodClientSecret,
  getFinancialSummary,
  detachPaymentMethod,
  detachBankAccount,
} from "../stripe";
import {
  fetchSubscriptionStatus,
  fetchSubscriptionSummary,
  chargeSubscriptionNow,
  cancelSubscription,
  resumeSubscription,
} from "../subscription";
import { resetTasksCache } from "../../vend/api/task/api";
import { useVendStore } from "../../vend/state/store";
import { useRoutePlannerStore } from "../../vend/state/routePlanner";
import { hasOrgAccess, isContactRole } from "../../vend/access";
import { clearPersistenceStorage } from "../../vend/navigation/persistenceKeys";
import { clearRememberedRoutes } from "../../vend/navigation/sectionHistory";
import { clearAuthenticationClientState } from "../../vend/sessionCleanup";
import { parseSigninError, presentSigninError } from "../authErrors";
import { safeAppPath } from "../authContinuation";
import {
  superuserBillingAccessEnabled,
  useSuperuserContextStore,
} from "../../vend/state/superuserContext";
import {
  accountContextRoleFlags,
  loadAccountContexts,
  selectedAccountContext,
  subscribeToAccountContextChange,
  useAccountContextStore,
} from "../../vend/state/accountContext";
import { ApiResponseError } from "../../apiError";
import { AUTH_SYNC_KEY } from "@vendistri/ui";

// third party
import { create } from "zustand";

export { AUTH_SYNC_KEY } from "@vendistri/ui";

const SUBSCRIPTION_MIN_AGE_MS = 15_000;
const AUTH_REFRESH_MIN_AGE_MS = 60_000;
export const EMPTY_BILLING_STATE = {
  financialSummary: null,
  financialSummaryLoading: false,
  financialSummaryError: null,
  billingSummary: null,
  billingStatus: null,
  subscriptionLoading: false,
  subscriptionError: null,
  subscriptionLastFetched: null,
  chargeNowLoading: false,
  chargeNowError: null,
  chargeNowResult: null,
  stripeLoading: false,
  stripeError: null,
};
let financialSummaryPromise: Promise<void> | null = null;
let subscriptionRefreshPromise: Promise<void> | null = null;
let subscriptionRefreshIsForced = false;
let queuedForcedSubscriptionRefresh: Promise<void> | null = null;
let authRefreshPromise: Promise<void> | null = null;
let authBootstrapRevision = 0;
let organizationLoadRevision = 0;

const isAbortError = (error: unknown) =>
  Boolean(error && typeof error === "object" && (error as any).name === "AbortError");

const requireBillingAccess = () => {
  if (superuserBillingAccessEnabled()) return;
  throw new Error("Enable billing mode before using billing support.");
};

const broadcastAuthChange = (event: "signin" | "signout") => {
  try {
    window.localStorage.setItem(
      AUTH_SYNC_KEY,
      JSON.stringify({ event, at: Date.now() }),
    );
  } catch (_error) {
    // ignore storage failures
  }
};

const clearLocalUserSessionState = (
  set: (
    partial:
      | Partial<UserState>
      | ((state: UserState) => Partial<UserState>),
  ) => void,
) => {
  authBootstrapRevision += 1;
  useSuperuserContextStore.getState().reset();
  useAccountContextStore.getState().reset();
  clearAuthenticationClientState();
  set({
    machines: [],
    locations: [],
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    photoUrl: null,
    photoRevision: 0,
    phone: "",
    sidebarRestoreLastVisited: false,
    sidebarShowRememberedShortcuts: true,
    isAdmin: false,
    isOwner: false,
    isStaff: false,
    isContact: false,
    canViewCalendar: false,
    isSuperuser: false,
    isOperator: false,
    viewMode: "org",
    accountMode: null,
    isAuthenticated: false,
    initialized: true,
    securityChallenge: {},
    stripeLoading: false,
    stripeError: null,
    financialSummary: null,
    financialSummaryLoading: false,
    financialSummaryError: null,
    organization: null,
    organizationLoading: false,
    organizationError: null,
    billingSummary: null,
    billingStatus: null,
    subscriptionLoading: false,
    subscriptionError: null,
    subscriptionLastFetched: null,
    authLastRefreshedAt: null,
    chargeNowLoading: false,
    chargeNowError: null,
    chargeNowResult: null,
  });
  try {
    window.localStorage.removeItem("vendistri.securityChallenge");
    window.localStorage.removeItem("vendistri.viewMode");
    window.localStorage.removeItem("vend.liveSalesDonut");
    window.localStorage.removeItem("locations.viewState");
    window.localStorage.removeItem("routes.selectedLocationId");
    window.localStorage.removeItem("routes.selectedDate");
    window.localStorage.removeItem("routes.startPosition");
    window.localStorage.removeItem("routePlanner.startPosition");
    window.localStorage.removeItem("vend:locationScheduling:viewMode");
  } catch (error) {
    console.error(error);
  }
};

export const useUserStore = create<UserState>((set, get) => ({
  id: "",
  email: "",
  firstName: "",
  lastName: "",
  photoUrl: null,
  photoRevision: 0,
  phone: "",
  sidebarRestoreLastVisited: false,
  sidebarShowRememberedShortcuts: true,
  machines: [],
  locations: [],
  isAdmin: false,
  isOwner: false,
  isStaff: false,
  isContact: false,
  canViewCalendar: false,
  isSuperuser: false,
  isOperator: false,
  viewMode: "org",
  accountMode: null,

  isAuthenticated: false,
  initialized: false,
  loading: { on: false },

  securityChallenge: {},

  stripeLoading: false,
  stripeError: null,
  financialSummary: null,
  financialSummaryLoading: false,
  financialSummaryError: null,
  organization: null,
  organizationLoading: false,
  organizationError: null,
  billingSummary: null,
  billingStatus: null,
  subscriptionLoading: false,
  subscriptionError: null,
  subscriptionLastFetched: null,
  authLastRefreshedAt: null,
  chargeNowLoading: false,
  chargeNowError: null,
  chargeNowResult: null,

  init: async (): Promise<void> => {
    let revision = ++authBootstrapRevision;
    try {
      const previousState = get();
      const previousUserId = previousState.id;
      const user = await getUser();
      if (revision !== authBootstrapRevision || get().id !== previousUserId) return;
      const identityChanged = Boolean(
        previousUserId && previousUserId !== user.id,
      );
      if (identityChanged) {
        // Browser tabs share one backend session cookie. If another tab signs in
        // as a different user, clear the stale vend/user state before hydrating
        // this tab with the new authenticated user.
        abortAllRequests();
        clearLocalUserSessionState(set);
        revision = authBootstrapRevision;
      }
      const storedView = (() => {
        try {
          const value = window.localStorage.getItem("vendistri.viewMode");
          return value === "contact" || value === "org" ? value : null;
        } catch (_error) {
          return null;
        }
      })();
      const isContact = isContactRole(user);
      const hasOrg = hasOrgAccess(user);
      const allowStored =
        storedView === "org"
          ? hasOrg
          : storedView === "contact"
            ? isContact
            : false;
      const nextView = allowStored ? storedView! : !hasOrg && isContact ? "contact" : "org";
      const existingContext = previousState.initialized && !identityChanged ? selectedAccountContext() : null;
      set({
        ...user,
        ...(existingContext ? accountContextRoleFlags(existingContext) : {}),
        photoUrl: !identityChanged && get().photoRevision !== previousState.photoRevision ? get().photoUrl : user.photoUrl ?? null,
        viewMode: existingContext ? accountContextRoleFlags(existingContext).viewMode : nextView,
        isAuthenticated: true,
        // Background refreshes for the same identity must not tear down the
        // authenticated application shell. Only bootstrap or a real identity
        // replacement may enter the uninitialized state.
        initialized:
          previousState.initialized && !identityChanged && previousUserId === user.id,
        authLastRefreshedAt: Date.now(),
      });
      try {
        const accountContext = await loadAccountContexts(!user.isSuperuser);
        if (revision !== authBootstrapRevision || get().id !== user.id) return;
        set({ ...accountContextRoleFlags(accountContext), ...(!accountContext ? {
          organization: null, organizationLoading: false, organizationError: null, ...EMPTY_BILLING_STATE,
        } : {}) });
      } catch (error) {
        if (isAbortError(error) || revision !== authBootstrapRevision || get().id !== user.id) return;
        console.error(error);
      }
      set({ initialized: true });
      try {
        const stored = window.localStorage.getItem(
          "vendistri.securityChallenge",
        );
        if (stored) {
          const parsed = JSON.parse(stored) as {
            userId?: string;
            challengeId?: string;
            destinationHint?: string;
            canResendAt?: number;
            action?: "change_email";
            createdAt?: number;
          };
          const ttlMs = 10 * 60 * 1000;
          const createdAt = parsed?.createdAt || 0;
          if (
            parsed?.userId === user.id &&
            parsed?.challengeId &&
            Date.now() - createdAt < ttlMs
          ) {
            set({
              securityChallenge: {
                action: parsed.action,
                challengeId: parsed.challengeId,
                destinationHint: parsed.destinationHint,
                canResendAt: parsed.canResendAt,
              },
            });
          } else {
            window.localStorage.removeItem("vendistri.securityChallenge");
          }
        }
      } catch (error) {
        console.error(error);
      }
      if (!getCookie("csrftoken")) {
        setCsrfToken().catch(() => {});
      }
    } catch (error) {
      if (isAbortError(error) || revision !== authBootstrapRevision) return;
      const previousState = get();
      if (
        previousState.isAuthenticated &&
        !(error instanceof ApiResponseError && error.status === 401)
      ) {
        set({ authLastRefreshedAt: Date.now(), initialized: true });
        return;
      }
      clearLocalUserSessionState(set);
      set({ authLastRefreshedAt: Date.now() });
    }
  },

  refreshAuthIfStale: async (options) => {
    if (authRefreshPromise) return authRefreshPromise;
    const state = get();
    if (!options?.force && state.authLastRefreshedAt) {
      const elapsed = Date.now() - state.authLastRefreshedAt;
      const minAgeMs = options?.minAgeMs ?? AUTH_REFRESH_MIN_AGE_MS;
      if (elapsed < minAgeMs) return;
    }
    authRefreshPromise = get().init().finally(() => {
      authRefreshPromise = null;
    });
    return authRefreshPromise;
  },

  applyPhoto: (userId, photoUrl) => {
    if (!userId || get().id !== userId) return;
    set({ photoUrl, photoRevision: (get().photoRevision || 0) + 1 });
  },

  updateProfile: async (payload) => {
    const previous = get();
    await updateUserProfile({
      firstName: payload.firstName,
      lastName: payload.lastName,
      ...(payload.phone !== undefined
        ? { phone: normalizePhone(payload.phone || "") || "" }
        : {}),
      sidebarRestoreLastVisited: payload.sidebarRestoreLastVisited,
      sidebarShowRememberedShortcuts: payload.sidebarShowRememberedShortcuts,
    });
    if (get().id !== previous.id) throw new Error("The signed-in account changed. Please try again.");
    const nextFirstName = payload.firstName ?? previous.firstName;
    const nextLastName = payload.lastName ?? previous.lastName;
    const nextPhone =
      payload.phone !== undefined
        ? normalizePhone(payload.phone || "") || ""
        : previous.phone || "";
    set({
      firstName: nextFirstName,
      lastName: nextLastName,
      phone: nextPhone,
      sidebarRestoreLastVisited:
        payload.sidebarRestoreLastVisited ?? previous.sidebarRestoreLastVisited,
      sidebarShowRememberedShortcuts:
        payload.sidebarShowRememberedShortcuts ??
        previous.sidebarShowRememberedShortcuts,
    });
    const vendState = useVendStore.getState();
    if (previous.id && vendState.locations.length) {
      const nextLocations = vendState.locations.map((location) => {
        const contacts = location.contacts || [];
        let changed = false;
        const nextContacts = contacts.map((contact) => {
          if (contact.userId !== previous.id) return contact;
          changed = true;
          return {
            ...contact,
            firstName: nextFirstName || contact.firstName,
            lastName: nextLastName || contact.lastName,
            phone: nextPhone,
          };
        });
        return changed ? { ...location, contacts: nextContacts } : location;
      });
      vendState.setLocations(nextLocations);
    }
  },

  loadOrganization: async () => {
    if (isDemoActive()) return;
    const state = get();
    const contextId = useAccountContextStore.getState().selectedContextId;
    if (state.organizationLoading) return;
    const revision = ++organizationLoadRevision;
    let invalidated = false;
    const unsubscribe = subscribeToAccountContextChange(() => { invalidated = true; });
    set({ organizationLoading: true, organizationError: null });
    try {
      const org = await fetchOrganization();
      if (invalidated || get().id !== state.id || useAccountContextStore.getState().selectedContextId !== contextId) return;
      set({ organization: org });
      useAccountContextStore
        .getState()
        .updateOrganizationName(org.id, org.title);
    } catch (e: any) {
      if (!invalidated && get().id === state.id && useAccountContextStore.getState().selectedContextId === contextId) set({ organizationError: e?.message || "Failed to load organization" });
    } finally {
      unsubscribe();
      if (revision === organizationLoadRevision && get().id === state.id) set({ organizationLoading: false });
    }
  },

  updateOrganizationLogo: async (file) => {
    assertBillingWriteAllowed(get().billingStatus);
    const updated = await uploadOrganizationLogo(file);
    set((state) => ({
      organization: state.organization
        ? { ...state.organization, ...updated, useDefaultLogo: false }
        : state.organization,
    }));
  },

  removeOrganizationLogo: async () => {
    assertBillingWriteAllowed(get().billingStatus);
    await removeOrganizationLogo();
    set((state) => ({
      organization: state.organization
        ? {
            ...state.organization,
            logoUrl: null,
            useDefaultLogo: true,
          }
        : state.organization,
    }));
  },

  setDefaultOrganizationLogo: async () => {
    assertBillingWriteAllowed(get().billingStatus);
    await setDefaultOrganizationLogo();
    set((state) => ({
      organization: state.organization
        ? {
            ...state.organization,
            logoUrl: null,
            useDefaultLogo: true,
          }
        : state.organization,
    }));
  },

  updateOrganization: async (payload) => {
    assertBillingWriteAllowed(get().billingStatus);
    const updated = await updateOrganization(payload);
    set({ organization: updated });
    if ("defaultLocationLeadAssigneeId" in payload)
      window.dispatchEvent(new CustomEvent("vendistri:growth-changed", { detail: { path: "/settings" } }));
    useAccountContextStore
      .getState()
      .updateOrganizationName(updated.id, updated.title);
    useSuperuserContextStore.getState().updateSelectedOrg({
      id: updated.id,
      title: updated.title,
    });
  },

  getStripePaymentMethodSecret: async () => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      return await getPaymentMethodClientSecret();
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to load Stripe" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  getStripeBankAccountSecret: async () => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      return await getBankAccountClientSecret();
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to load Stripe" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  attachStripePaymentMethod: async (paymentMethodId) => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      await attachPaymentMethod(paymentMethodId);
      await get().loadFinancialSummary();
      await get().refreshSubscription({ force: true });
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to attach payment method" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  attachStripeBankAccount: async (bankAccountId) => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      await attachBankAccount(bankAccountId);
      await get().loadFinancialSummary();
      await get().refreshSubscription({ force: true });
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to attach bank account" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  loadFinancialSummary: async () => {
    if (isDemoActive()) return;
    if (!superuserBillingAccessEnabled()) return;
    if (financialSummaryPromise) return financialSummaryPromise;
    const actorId = get().id;
    let current = true;
    const invalidate = () => {
      if (!current) return;
      current = false;
      financialSummaryPromise = null;
      set({ financialSummary: null, financialSummaryLoading: false, financialSummaryError: null });
    };
    const unsubscribe = subscribeToAccountContextChange(invalidate);
    const unsubscribeActor = useUserStore.subscribe(state => {
      if (state.id !== actorId) invalidate();
    });
    set({ financialSummaryLoading: true, financialSummaryError: null });
    financialSummaryPromise = (async () => {
      try {
        const summary = await getFinancialSummary();
        if (!current || get().id !== actorId) return;
        if (!superuserBillingAccessEnabled()) {
          set(EMPTY_BILLING_STATE);
          return;
        }
        set({ financialSummary: summary });
      } catch (e: any) {
        if (current && get().id === actorId) set({
          financialSummaryError: e?.message || "Failed to load payment summary",
        });
      } finally {
        unsubscribe();
        unsubscribeActor();
        if (current && get().id === actorId) {
          set({ financialSummaryLoading: false });
          financialSummaryPromise = null;
        }
      }
    })();
    return financialSummaryPromise;
  },

  detachStripePaymentMethod: async () => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      await detachPaymentMethod();
      await get().loadFinancialSummary();
      await get().refreshSubscription({ force: true });
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to remove payment method" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  detachStripeBankAccount: async () => {
    requireBillingAccess();
    set({ stripeLoading: true, stripeError: null });
    try {
      await detachBankAccount();
      await get().loadFinancialSummary();
      await get().refreshSubscription({ force: true });
    } catch (e: any) {
      set({ stripeError: e?.message || "Failed to remove bank account" });
      throw e;
    } finally {
      set({ stripeLoading: false });
    }
  },

  refreshSubscription: async (options) => {
    if (isDemoActive()) return;
    if (!superuserBillingAccessEnabled()) return;
    const force = options?.force === true;
    if (subscriptionRefreshPromise && (subscriptionRefreshIsForced || !force)) {
      return subscriptionRefreshPromise;
    }
    if (subscriptionRefreshPromise) {
      if (!queuedForcedSubscriptionRefresh) {
        queuedForcedSubscriptionRefresh = subscriptionRefreshPromise.finally(() =>
          get().refreshSubscription({ force: true }).finally(() => {
            queuedForcedSubscriptionRefresh = null;
          })
        );
      }
      return queuedForcedSubscriptionRefresh;
    }
    const state = get();
    if (!force && state.subscriptionLastFetched) {
      const elapsed = Date.now() - state.subscriptionLastFetched;
      const minAgeMs = options?.minAgeMs ?? SUBSCRIPTION_MIN_AGE_MS;
      if (elapsed < minAgeMs) return;
    }
    subscriptionRefreshIsForced = force;
    set({ subscriptionLoading: true, subscriptionError: null });
    subscriptionRefreshPromise = (async () => {
      try {
        const [billingSummary, billingStatus] = await Promise.all([
          fetchSubscriptionSummary(),
          fetchSubscriptionStatus(),
        ]);
        if (!superuserBillingAccessEnabled()) {
          set(EMPTY_BILLING_STATE);
          return;
        }
        set({
          billingSummary,
          billingStatus,
          subscriptionLastFetched: Date.now(),
        });
      } catch (e: any) {
        set({
          billingSummary: null,
          billingStatus: null,
          subscriptionError: e?.message || "Failed to load subscription",
          subscriptionLastFetched: Date.now(),
        });
      } finally {
        set({ subscriptionLoading: false });
        subscriptionRefreshPromise = null;
        subscriptionRefreshIsForced = false;
      }
    })();
    return subscriptionRefreshPromise;
  },

  chargeSubscriptionNow: async () => {
    requireBillingAccess();
    set({ chargeNowLoading: true, chargeNowError: null });
    try {
      const result = await chargeSubscriptionNow();
      set({ chargeNowResult: result });
      await get().refreshSubscription({ force: true });
      return result;
    } catch (e: any) {
      set({
        chargeNowError: e?.message || "Failed to charge subscription",
        chargeNowResult: null,
      });
      throw e;
    } finally {
      set({ chargeNowLoading: false });
    }
  },

  cancelSubscription: async () => {
    requireBillingAccess();
    await cancelSubscription();
    await get().refreshSubscription({ force: true });
  },

  resumeSubscription: async () => {
    requireBillingAccess();
    const status = await resumeSubscription();
    if (status.subscriptionState !== "active") {
      throw new Error("The subscription could not be resumed. Update the payment method and try again.");
    }
    await get().refreshSubscription({ force: true });
  },

  requestSecurityChallenge: async (params) => {
    const result = await requestSecurityChallenge(params);
    if (!result?.challengeId) {
      set({ securityChallenge: {} });
      try {
        window.localStorage.removeItem("vendistri.securityChallenge");
      } catch (error) {
        console.error(error);
      }
      return;
    }
    const challenge = {
      action: params.action,
      challengeId: result.challengeId,
      destinationHint: result.destinationHint,
      canResendAt: Date.now() + 60_000,
    };
    set({ securityChallenge: challenge });
    try {
      window.localStorage.setItem(
        "vendistri.securityChallenge",
        JSON.stringify({
          userId: get().id,
          createdAt: Date.now(),
          ...challenge,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  },

  confirmSecurityChallenge: async (params) => {
    const result = await confirmSecurityChallenge(params);
    if (result.status === "email_changed" && result.email) {
      set({ email: result.email });
    }
    set({ securityChallenge: {} });
    try {
      window.localStorage.removeItem("vendistri.securityChallenge");
    } catch (error) {
      console.error(error);
    }
    return result;
  },

  clearSecurityChallenge: () => {
    set({ securityChallenge: {} });
    try {
      window.localStorage.removeItem("vendistri.securityChallenge");
    } catch (error) {
      console.error(error);
    }
  },

  setViewMode: (mode) => {
    const currentMode = get().viewMode;
    if (currentMode !== mode) {
      resetTasksCache();
      useVendStore.setState({
        tasks: [],
        tasksHydrated: false,
        tasksLoading: false,
        tasksError: null,
        tasksFetchedAt: null,
        taskScopes: {},
      });
      useRoutePlannerStore.getState().reset();
      clearRememberedRoutes();
    }
    clearPersistenceStorage(sessionStorage);
    try {
      window.localStorage.setItem("vendistri.viewMode", mode);
    } catch (_error) {
      // ignore storage failures
    }
    set({ viewMode: mode });
  },

  signout: async (options) => {
    authBootstrapRevision += 1;
    suppressUnauthorizedFor(2000);
    abortAllRequests();
    useAccountContextStore.getState().reset();
    try {
      const res = await fetch(`${url}/signout`, {
        method: "POST",
        headers: {
          "X-CSRFToken": getCsrfToken() || (await setCsrfToken()),
        },
        credentials: "include",
      });
      if (!res.ok && res.status !== 401) {
        throw new Error(`Failed to sign out (${res.status})`);
      }
      abortAllRequests();
      // Set the destination before clearing identity: the router remounts for
      // the signed-out session, discarding mounted views without a page reload.
      window.history.replaceState(
        { idx: window.history.state?.idx, key: "signed-out", usr: { signedOut: true } },
        "",
        safeAppPath(options?.redirectTo || null) || "/",
      );
      clearLocalUserSessionState(set);
      broadcastAuthChange("signout");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  signup: (user, password) => {},

  signin: async (email, password) => {
    if (!email || !password) {
      return false;
    }

    authBootstrapRevision += 1;
    set({ loading: { on: true } });
    try {
      const res = await fetch(`${url}/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": await setCsrfToken(),
        },
        body: JSON.stringify({ email: normalizeIdentityEmail(email), password }),
        credentials: "include",
      });

      if (res.ok) {
        clearAuthenticationClientState();
        await get().init();
        broadcastAuthChange("signin");
        return true;
      } else {
        set({ isAuthenticated: false, initialized: true });
        const backendMessage = await parseSigninError(res, "Failed to sign in");
        throw new Error(presentSigninError(backendMessage));
      }
    } finally {
      set({ loading: { on: false } });
    }
  },
}));

/** Replace this tab's client session without signing out the shared cookie. */
export function replaceBrowserSession() {
  abortAllRequests();
  clearLocalUserSessionState(useUserStore.setState);
  window.location.replace("/");
}
